// GlobalAdjustFlow — the quick-actions "Adjust stock" journey from ANY page:
// pick an in-stock product for the given store, then hand off to the same
// AdjustStockModal the product page uses (reason codes, capability gating,
// audit and backorder fulfilment all unchanged).
import { useMemo, useState } from 'react';
import { EmptyState, Input, Modal, mono } from '@/foundation/primitives';
import { useStoreName } from '@/api/useStores';
import { useStoreProducts } from './api';
import { AdjustStockModal, type AdjustBatch } from './AdjustStockModal';

export function GlobalAdjustFlow({ storeId, onClose }: { storeId: string; onClose: () => void }) {
  // Store-scoped aggregation (products WITH stock at this store, batches
  // included). The plain /products catalogue is a 17k-row dm+d directory of
  // which pickers fetch only the first page — client-filtering it against
  // batches found nothing on a real deployment (the deployed-search bug).
  const { data: storeProducts = [] } = useStoreProducts('ampp', {}, storeId);
  const storeName = useStoreName();
  const [search, setSearch] = useState('');
  const [productId, setProductId] = useState<string | null>(null);

  // Only products with live stock at this store are adjustable (same rule as the
  // product page: depleted batches are corrected from the Batches page).
  const inStock = useMemo(() => {
    const q = search.trim().toLowerCase();
    return storeProducts
      .filter((sp) => sp.batches.some((b) => b.qtyOnHand > 0))
      .filter(
        (sp) => !q || sp.name.toLowerCase().includes(q) || (sp.sku ?? '').toLowerCase().includes(q),
      )
      .slice(0, 25);
  }, [storeProducts, search]);

  const picked = inStock.find((sp) => sp.productId === productId);
  if (picked) {
    const visibleBatches: AdjustBatch[] = picked.batches
      .filter((b) => b.qtyOnHand > 0)
      .map((b) => ({
        id: b.id,
        batchNo: b.batchNumber,
        qty: b.qtyOnHand,
        alloc: b.qtyAllocated,
        exp: b.expiryDate,
        bin: b.binLocation,
      }));
    return (
      <AdjustStockModal
        batches={visibleBatches}
        initialBatchId={visibleBatches[0].id}
        storeId={storeId}
        productName={picked.name}
        product={{ id: picked.productId, sku: picked.sku }}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      width={520}
      title="Adjust stock — pick a product"
      subtitle={`Products with stock at ${storeName(storeId)}. Applied immediately, fully audited.`}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search product…"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <div
          style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
        >
          {inStock.map((sp) => (
            <button
              key={sp.productId}
              type="button"
              onClick={() => setProductId(sp.productId)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                border: 'none',
                borderBottom: '1px solid var(--divider)',
                background: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--fs-body)',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                  }}
                >
                  {sp.name}
                </div>
                <div
                  style={{ ...mono, fontSize: 'var(--fs-caption)', color: 'var(--text-secondary)' }}
                >
                  {sp.sku}
                </div>
              </div>
              <span
                style={{
                  fontSize: 'var(--fs-body-sm)',
                  fontWeight: 700,
                  color: 'var(--text-secondary)',
                }}
              >
                {sp.totalQty} on hand
              </span>
            </button>
          ))}
          {!inStock.length && (
            <EmptyState
              icon="box"
              title="No products with stock"
              body="Nothing matches at this store — use Add stock to receive a product first."
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
